import type { Location } from 'history';
import { matchPath } from 'react-router-dom';

export type AllowedRoutePattern =
  | string
  | { path: string; exact?: boolean; strict?: boolean };

/** Strip trailing slashes (except root) for stable comparisons. */
export function normalizePathname(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.replace(/\/+$/, '');
  }
  return pathname;
}

function toMatchOptions(pattern: AllowedRoutePattern): {
  path: string;
  exact: boolean;
  strict: boolean;
} {
  if (typeof pattern === 'string') {
    return {
      path: normalizePathname(pattern),
      exact: true,
      strict: false
    };
  }
  return {
    path: normalizePathname(pattern.path),
    exact: pattern.exact ?? true,
    strict: pattern.strict ?? false
  };
}

/** Returns a predicate suitable for UnsavedChangesProtection.allowNavigation. */
export function createAllowedNavigationMatcher(
  patterns: AllowedRoutePattern[]
): (location: Location) => boolean {
  const matchOptions = patterns.map(toMatchOptions);

  if (matchOptions.length === 0) {
    return () => false;
  }

  return (location: Location) => {
    const pathname = normalizePathname(location.pathname);
    return matchOptions.some(options => matchPath(pathname, options) != null);
  };
}

/**
 * Lets through navigation that keeps `pathname` (only the query or hash
 * change), for hosts that survive exactly that.
 */
export function createSamePathnameMatcher(
  pathname: string
): (location: Location) => boolean {
  const current = normalizePathname(pathname);
  return location => normalizePathname(location.pathname) === current;
}
